import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Camera, Upload, X, Loader2, Wine, SwitchCamera, ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Scan() {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const handleFile = useCallback((file) => {
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WEBP image');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be smaller than 10MB');
      return;
    }
    
    setImage(file);
    setImagePreview(URL.createObjectURL(file));
    setShowCamera(false);
    stopCamera();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  }, [handleFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    handleFile(file);
  };

  const clearImage = () => {
    setImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }
  };

  // Start camera stream
  const startCamera = async () => {
    setCameraError(null);
    try {
      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      
      setShowCamera(true);
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError('Could not access camera. Please check permissions or use file upload.');
      // Fallback to file input with camera capture
      if (cameraInputRef.current) {
        cameraInputRef.current.click();
      }
    }
  };

  // Stop camera stream
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
  };

  // Switch between front and back camera
  const switchCamera = async () => {
    const newMode = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(newMode);
    stopCamera();
    setTimeout(() => {
      startCamera();
    }, 100);
  };

  // Capture photo from video stream
  const capturePhoto = () => {
    if (!videoRef.current) return;
    
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);
    
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], 'camera-capture.jpg', { type: 'image/jpeg' });
        handleFile(file);
      }
    }, 'image/jpeg', 0.9);
  };

  const handleScan = async () => {
    if (!image) {
      toast.error('Please capture or upload an image first');
      return;
    }

    setLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', image);
      
      const response = await axios.post(`${API}/scan`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Store results and navigate
      sessionStorage.setItem('scanResults', JSON.stringify(response.data));
      toast.success(`Found ${response.data.detected_bottles?.length || 0} bottles!`);
      navigate('/results');
    } catch (error) {
      console.error('Scan error:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze image');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="scan-page">
      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="text-4xl md:text-5xl font-bold text-[#F5F5F5] mb-3">
          Scan Your Bar
        </h1>
        <p className="text-[#A3A3A3] text-lg max-w-md mx-auto">
          Take a photo of your liquor collection and we'll find cocktails you can make
        </p>
      </div>

      {/* Camera/Upload Zone */}
      <div className="mb-8">
        {imagePreview ? (
          <div className="relative rounded-2xl overflow-hidden animate-fadeIn">
            <img
              src={imagePreview}
              alt="Bar preview"
              className="w-full h-[400px] object-cover"
            />
            <button
              onClick={clearImage}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-black/80 transition-colors"
              data-testid="clear-image-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        ) : showCamera ? (
          <div className="relative rounded-2xl overflow-hidden animate-fadeIn bg-black">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-[400px] object-cover"
            />
            {/* Camera Controls */}
            <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-4">
              <Button
                onClick={stopCamera}
                variant="outline"
                size="sm"
                className="bg-black/60 border-white/30 text-white hover:bg-black/80 rounded-full"
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <button
                onClick={capturePhoto}
                className="w-16 h-16 rounded-full bg-white flex items-center justify-center hover:bg-gray-200 transition-colors"
                data-testid="capture-btn"
              >
                <div className="w-12 h-12 rounded-full border-4 border-[#D4AF37]" />
              </button>
              <Button
                onClick={switchCamera}
                variant="outline"
                size="sm"
                className="bg-black/60 border-white/30 text-white hover:bg-black/80 rounded-full"
              >
                <SwitchCamera className="w-4 h-4 mr-2" />
                Flip
              </Button>
            </div>
          </div>
        ) : (
          <div
            className={`upload-zone rounded-2xl h-[400px] flex flex-col items-center justify-center transition-all ${
              dragging ? 'dragging' : ''
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            data-testid="upload-zone"
          >
            <div className="flex gap-8 mb-8">
              {/* Camera Option */}
              <button
                onClick={startCamera}
                className="flex flex-col items-center gap-3 p-6 rounded-2xl bg-[#D4AF37]/5 border border-[#D4AF37]/20 hover:bg-[#D4AF37]/10 hover:border-[#D4AF37]/40 transition-all group"
                data-testid="camera-btn"
              >
                <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center group-hover:bg-[#D4AF37]/20 transition-colors">
                  <Camera className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <span className="text-[#F5F5F5] font-medium">Take Photo</span>
              </button>
              
              {/* Upload Option */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex flex-col items-center gap-3 p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all group"
                data-testid="upload-btn"
              >
                <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-white/10 transition-colors">
                  <ImageIcon className="w-8 h-8 text-[#A3A3A3]" />
                </div>
                <span className="text-[#A3A3A3] font-medium">Upload Image</span>
              </button>
            </div>
            
            <p className="text-[#525252] text-sm mb-2">
              or drag and drop an image here
            </p>
            <p className="text-[#525252] text-xs">
              Supports JPEG, PNG, WEBP (max 10MB)
            </p>
            
            {cameraError && (
              <p className="mt-4 text-red-400 text-sm text-center max-w-sm">
                {cameraError}
              </p>
            )}
          </div>
        )}
        
        {/* Hidden file inputs */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileChange}
          className="hidden"
          data-testid="file-input"
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileChange}
          className="hidden"
          data-testid="camera-input"
        />
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-4">
        <Button
          onClick={handleScan}
          disabled={!image || loading}
          className="w-full bg-[#D4AF37] text-black hover:bg-[#F4CF57] font-bold rounded-full py-6 text-lg gold-glow btn-press disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="scan-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <Camera className="w-5 h-5 mr-2" />
              Scan & Find Cocktails
            </>
          )}
        </Button>
        
        {image && (
          <Button
            variant="outline"
            onClick={clearImage}
            disabled={loading}
            className="w-full border-white/20 text-[#A3A3A3] hover:text-[#F5F5F5] hover:border-white/30 rounded-full py-6 btn-press"
          >
            <X className="w-5 h-5 mr-2" />
            Clear & Start Over
          </Button>
        )}
      </div>

      {/* Tips */}
      <div className="mt-10 glass rounded-2xl p-6">
        <h3 className="text-lg font-bold text-[#F5F5F5] mb-4 flex items-center gap-2">
          <Wine className="w-5 h-5 text-[#D4AF37]" />
          Tips for Best Results
        </h3>
        <ul className="space-y-3 text-[#A3A3A3] text-sm">
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2" />
            Make sure all bottles are visible and labels are facing the camera
          </li>
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2" />
            Good lighting helps identify bottles more accurately
          </li>
          <li className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2" />
            Include mixers like juices, sodas, and bitters for more cocktail options
          </li>
        </ul>
      </div>
    </div>
  );
}
